import { getCroppedImageUrl } from '@/services/image-service';
import { Game } from '@/types/GameType';
import { useT, useLocale } from '@/i18n/useLanguage';
import { Box, Flex, Text, Badge } from '@chakra-ui/react';
import { Card } from '@chakra-ui/react/card';
import { Image } from '@chakra-ui/react/image';
import { Skeleton } from '@chakra-ui/react/skeleton';
import { HStack } from '@chakra-ui/react/stack';
import { Heading } from '@chakra-ui/react/typography';
import { Link } from 'react-router';
import MetaCritic from '../common/MetaCritic';
import PlatformIcons from '../platform/PlatformIcons';
import { SkeletonText } from '../common/skeleton';
import { LuStar } from 'react-icons/lu';

interface Props {
	game?: Game | null;
}

const GameCards = ({ game }: Props) => {
	const t = useT();
	const locale = useLocale();
	const dateLocale = locale === 'sv' ? 'sv-SE' : 'en-US';

	return (
		<Card.Root
			borderRadius="2xl"
			overflow="hidden"
			width="100%"
			bg="bg.muted"
			borderWidth="1px"
			borderColor="border"
			transition="transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease"
			position="relative"
			_hover={{
				transform: 'translateY(-4px)',
				boxShadow: '0 12px 40px rgba(139, 92, 246, 0.15)',
				borderColor: { _dark: 'whiteAlpha.300', _light: 'brand.200' },
			}}
		>
			<Link to={`/games/${game?.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
				{!game ? (
					<Skeleton height="180px" />
				) : (
					<Box position="relative" overflow="hidden">
						<Image
							src={getCroppedImageUrl(game.background_image)}
							objectFit="cover"
							w="100%"
							h="180px"
							alt={game.name}
						/>
						<Box
							position="absolute"
							inset={0}
							bgGradient="to-t"
							gradientFrom="blackAlpha.800"
							gradientTo="transparent"
							opacity={0.7}
						/>
						{game.rating > 0 && (
							<HStack
								position="absolute"
								top={3}
								right={3}
								bg="blackAlpha.700"
								backdropFilter="blur(8px)"
								px={2.5}
								py={1}
								rounded="full"
								gap={1}
								fontSize="xs"
								fontWeight="700"
								color="white"
							>
								<LuStar size={12} fill="#fbbf24" color="#fbbf24" />
								{game.rating.toFixed(1)}
							</HStack>
						)}
					</Box>
				)}

				<Card.Body padding={4} gap={2}>
					{!game ? (
						<SkeletonText noOfLines={3} />
					) : (
						<>
							<HStack justifyContent="space-between" align="center">
								<PlatformIcons
									platforms={game.parent_platforms?.map(({ platform }) => platform) ?? []}
								/>
								<MetaCritic score={game.metacritic} />
							</HStack>

							<Heading size="sm" fontWeight="700" lineHeight="short" lineClamp={2} minH="2.5em">
								{game.name}
							</Heading>

							{game.genres && game.genres.length > 0 && (
								<Flex gap={1.5} flexWrap="wrap" mt={1}>
									{game.genres.slice(0, 2).map((g) => (
										<Badge
											key={g.id}
											size="sm"
											variant="subtle"
											colorPalette="purple"
											rounded="full"
											fontSize="10px"
										>
											{g.name}
										</Badge>
									))}
								</Flex>
							)}

							<Text fontSize="xs" color="fg.muted" mt={1}>
								{game.released
									? new Date(game.released).toLocaleDateString(dateLocale, {
											year: 'numeric',
											month: 'short',
											day: 'numeric',
									  })
									: t('tba')}
								{game.playtime > 0 && ` · ${game.playtime}${t('hAvgShort')}`}
							</Text>
						</>
					)}
				</Card.Body>
			</Link>
		</Card.Root>
	);
};

export default GameCards;
